/**
 * "Ready to ship" badge over product images.
 *
 * snippets/ready-to-ship-badge.liquid renders the badge with the ids of the variants that qualify
 * (same condition as the PDP "In Stock. Ready to ship!" message); here we only show/hide it for the
 * variant that is currently selected:
 *  - PDP: the theme's <variant-options> fires `variantChangeEnd` with the new `currentVariant`.
 *  - PLP card: the variant-swatch-king app rewrites the card link (`?variant=ID`) on first load and
 *    on every swatch click, so we follow that link.
 */

const BADGE_SELECTOR = '[data-ready-to-ship]';
const CARD_SELECTOR = '.card__container';

const qualifies = (badge, variantId) =>
  Boolean(variantId) && badge.dataset.variantIds.includes(`,${variantId},`);

function getCardVariantId(card) {
  const href = card
    .querySelector('a.card[href*="variant="]')
    ?.getAttribute('href');
  if (href) {
    return new URL(href, window.location.origin).searchParams.get('variant');
  }
  return card.querySelector('input[name="id"]')?.value;
}

function updateCard(card) {
  const badge = card.querySelector(BADGE_SELECTOR);
  const variantId = getCardVariantId(card);
  // Variant not known yet: keep the state rendered by Liquid (default variant)
  if (!badge || !variantId) return;

  badge.hidden = !qualifies(badge, variantId);
}

function collectCards(mutations) {
  const cards = new Set();

  mutations.forEach((mutation) => {
    // A swatch click changes the card link / quick-add input
    const card = mutation.target.closest?.(CARD_SELECTOR);
    if (card) cards.add(card);

    // Whole cards added at once (facet filters / pagination re-render the grid)
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      if (node.matches(CARD_SELECTOR)) cards.add(node);
      node.querySelectorAll(CARD_SELECTOR).forEach((el) => cards.add(el));
    });
  });

  return cards;
}

function initCards() {
  document.querySelectorAll(CARD_SELECTOR).forEach(updateCard);

  // Toggling `hidden` is not observed (only href/value attributes), so this can't loop
  new MutationObserver((mutations) => {
    collectCards(mutations).forEach(updateCard);
  }).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['href', 'value'],
  });
}

function initProduct() {
  document.addEventListener('variantChangeEnd', (event) => {
    // `currentVariant` is undefined when the option combination doesn't exist
    const variantId = event.target.currentVariant?.id;

    document
      .querySelectorAll(`gallery-section ${BADGE_SELECTOR}`)
      .forEach((badge) => {
        // Ignore the quick-add modal variant picker (a different <section>)
        if (!badge.closest('section')?.contains(event.target)) return;
        badge.hidden = !qualifies(badge, variantId);
      });
  });
}

function init() {
  initCards();
  initProduct();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
